import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Car, Users, Fuel, Calendar, Share2, CheckCircle, Clock, Navigation, Package, Check, Star, ShieldCheck, Route, Send, LogIn } from 'lucide-react';
import appConfig from '../../config/appConfig';
import SEO from '../../components/SEO';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';

// Map a vehicle serviceType to how it should be shown under "Our Services"
const SERVICE_META = {
  rental: { label: 'Vehicle Rental', desc: 'Daily, weekly, monthly', icon: Car },
  daily: { label: 'Daily Rental', desc: 'Per-day hire', icon: Calendar },
  'km-based': { label: 'Pay Per KM', desc: 'Charged by distance', icon: Navigation },
  outstation: { label: 'Outstation Trips', desc: 'One-way & round trips', icon: Navigation },
  local: { label: 'Local Trips', desc: 'In-city travel', icon: MapPin },
  sale: { label: 'Vehicles For Sale', desc: 'Buy a vehicle', icon: Car }
};

const StarRow = ({ value = 0, size = 'h-4 w-4', color = '#f59e0b' }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={size} style={{ color, fill: i <= Math.round(value) ? color : 'transparent' }} />
    ))}
  </span>
);

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
};

const titleCase = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, c => c.toUpperCase());
};

// Lighten/darken a hex color by a percent (-100..100)
const shade = (hex, percent) => {
  if (!hex || hex[0] !== '#') return hex;
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) + Math.round(2.55 * percent);
  let g = ((num >> 8) & 0xff) + Math.round(2.55 * percent);
  let b = (num & 0xff) + Math.round(2.55 * percent);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1);
};

const TravelsProfile = () => {
  const { ownerId } = useParams();
  const location = useLocation();
  const { user: authUser, token } = useAuth();
  const [travels, setTravels] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [brand, setBrand] = useState('#3B82F6');
  const [badges, setBadges] = useState({});
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState({ averageRating: 0, totalReviews: 0 });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  // Review form
  const [reviewForm, setReviewForm] = useState({ customerName: '', rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const fetchReviews = useCallback(async () => {
    try {
      const res = await fetch(API_BASE + '/reviews/owner-public/' + ownerId);
      const data = await res.json();
      if (data.success) {
        setReviews(data.reviews || []);
        setRating({ averageRating: data.averageRating || 0, totalReviews: data.totalReviews || 0 });
      }
    } catch (e) { /* ignore */ }
  }, [ownerId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [travelsRes, vehiclesRes, packagesRes] = await Promise.all([
          fetch(API_BASE + '/users/travels-info/' + ownerId),
          fetch(API_BASE + '/cars/owner-public/' + ownerId),
          fetch(API_BASE + '/packages/owner-public/' + ownerId)
        ]);
        const travelsData = await travelsRes.json();
        const vehiclesData = await vehiclesRes.json();
        const packagesData = await packagesRes.json();

        if (travelsData.success) {
          setTravels(travelsData.travelsInfo || travelsData.data);
          if (travelsData.branding?.primaryColor) setBrand(travelsData.branding.primaryColor);
          if (travelsData.badges) setBadges(travelsData.badges);
        }
        if (vehiclesData.success) setVehicles(vehiclesData.cars || []);
        if (packagesData.success) setPackages(packagesData.packages || []);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    fetchReviews();
  }, [ownerId, fetchReviews]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!authUser) {
      setReviewError('Please log in to write a review.');
      return;
    }
    setSubmitting(true);
    setReviewError('');
    try {
      const res = await fetch(API_BASE + '/reviews/owner/' + ownerId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ rating: reviewForm.rating, comment: reviewForm.comment })
      });
      const data = await res.json();
      if (data.success) {
        setReviewDone(true);
        // Keep form pre-filled with the just-submitted review so the user
        // can see/edit it; the "Update Review" button reflects it next time.
        fetchReviews();
      } else {
        setReviewError(data.message || 'Could not submit review.');
      }
    } catch (err) {
      setReviewError('Could not submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // When the user is logged in, fetch their existing review for this owner
  // (if any) so we can prefill the form with "Update Review" mode.
  const [hasExistingReview, setHasExistingReview] = useState(false);
  useEffect(() => {
    if (!authUser || !token || !ownerId) {
      setHasExistingReview(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(API_BASE + '/reviews/owner/' + ownerId + '/mine', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (cancelled) return;
        if (data?.success && data.review) {
          setReviewForm({
            customerName: data.review.customerName || authUser.name || '',
            rating: data.review.rating || 5,
            comment: data.review.comment || ''
          });
          setHasExistingReview(true);
        } else {
          setReviewForm(f => ({ ...f, customerName: authUser.name || '' }));
          setHasExistingReview(false);
        }
      } catch (e) {
        // silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [authUser, token, ownerId]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: (travels?.name || 'Travels'), url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const vehicleTypes = ['all', ...new Set(vehicles.map(v => v.vehicleType || 'car'))];
  const filteredVehicles = selectedType === 'all' ? vehicles : vehicles.filter(v => (v.vehicleType || 'car') === selectedType);

  const brandDark = shade(brand, -22);
  const heroGradient = { backgroundImage: `linear-gradient(135deg, ${brand}, ${brandDark})` };
  // Hero backdrop: owner's cover image first, then a vehicle photo, then
  // the platform's default branded cover (configured in appConfig).
  const coverImage = travels?.coverImage?.url
    || vehicles.flatMap(v => (v.images || []).filter(Boolean))[0]
    || appConfig.defaultTravelsCover
    || null;

  // Real services: unique serviceTypes actually offered across this agency's vehicles
  const offeredServiceKeys = [...new Set(vehicles.flatMap(v => v.serviceTypes || []))].filter(k => SERVICE_META[k]);
  const services = offeredServiceKeys.length > 0
    ? offeredServiceKeys.map(k => ({ key: k, ...SERVICE_META[k] }))
    : [{ key: 'rental', ...SERVICE_META.rental }];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 rounded-full animate-spin mx-auto mb-3" style={{ borderColor: brand, borderTopColor: 'transparent' }}></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!travels) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Car className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Travels Not Found</h2>
          <p className="text-gray-500 text-sm mb-4">This profile doesn't exist or has been removed.</p>
          <Link to="/browse" className="text-blue-600 hover:underline text-sm">Browse Vehicles</Link>
        </div>
      </div>
    );
  }

  const initial = travels.name?.charAt(0)?.toUpperCase() || 'T';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={travels.name}
        description={travels.description || `Book vehicles, tour packages & rentals from ${travels.name}${travels.address?.city ? ' in ' + travels.address.city : ''}.`}
        image={travels.coverImage?.url || travels.logo?.url}
        path={`/travels/${ownerId}`}
      />
      {/* Header — agency branding */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/home" className="text-lg sm:text-xl font-bold" style={{ color: brand }}>{appConfig.name}</Link>
          <button onClick={handleShare} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white flex-shrink-0" style={{ backgroundColor: brand }}>
            {copied ? <><CheckCircle className="h-4 w-4" /> Copied!</> : <><Share2 className="h-4 w-4" /> Share</>}
          </button>
        </div>
      </div>

      {/* Hero / Travels Info — cinematic */}
      <div className="relative overflow-hidden text-white" style={heroGradient}>
        {/* Backdrop photo (slow zoom) with a brand tint that still lets the photo show */}
        {coverImage && (
          <div className="absolute inset-0">
            <img src={coverImage} alt="" className="w-full h-full object-cover animate-slow-zoom" />
            {/* Lighter, left-biased tint: readable text on the left, photo visible on the right */}
            <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(105deg, ${brandDark}D9 0%, ${brand}99 50%, ${brand}33 100%)` }} />
          </div>
        )}
        {/* Bottom vignette so it melts into the page */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/40 to-transparent" />

        <div className="relative max-w-5xl mx-auto px-4 py-16 sm:py-24">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 sm:gap-8">
            {/* Logo */}
            <div className="animate-fade-up w-32 h-32 sm:w-44 sm:h-44 rounded-3xl bg-white/15 backdrop-blur-sm ring-1 ring-white/30 shadow-2xl flex items-center justify-center overflow-hidden flex-shrink-0">
              {travels.logo?.url ? (
                <img src={travels.logo.url} alt={travels.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-5xl sm:text-7xl font-bold text-white">{initial}</span>
              )}
            </div>
            {/* Info */}
            <div className="text-center sm:text-left flex-1">
              <p className="animate-fade-up delay-100 uppercase tracking-[0.2em] text-xs sm:text-sm text-white/70 mb-2 font-medium">Premium Travels &amp; Rentals</p>
              <h1 className="animate-fade-up delay-100 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-3 drop-shadow-lg">{travels.name}</h1>
              {travels.description && <p className="animate-fade-up delay-200 text-white/85 text-sm sm:text-base mb-4 max-w-xl">{travels.description}</p>}
              <div className="animate-fade-up delay-300 flex flex-wrap justify-center sm:justify-start gap-2 text-sm">
                {travels.address?.city && (
                  <span className="flex items-center gap-1 bg-white/15 backdrop-blur-sm ring-1 ring-white/20 px-3 py-1.5 rounded-full">
                    <MapPin className="h-3.5 w-3.5" /> {travels.address.city}{travels.address.state ? ', ' + travels.address.state : ''}
                  </span>
                )}
                <span className="flex items-center gap-1 bg-white/15 backdrop-blur-sm ring-1 ring-white/20 px-3 py-1.5 rounded-full">
                  <Car className="h-3.5 w-3.5" /> {vehicles.length} Vehicles
                </span>
                {rating.totalReviews > 0 && (
                  <span className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm ring-1 ring-white/20 px-3 py-1.5 rounded-full">
                    <Star className="h-3.5 w-3.5" style={{ color: '#fbbf24', fill: '#fbbf24' }} /> {rating.averageRating} <span className="text-white/70">({rating.totalReviews})</span>
                  </span>
                )}
                {travels.workingHours && (
                  <span className="flex items-center gap-1 bg-white/15 backdrop-blur-sm ring-1 ring-white/20 px-3 py-1.5 rounded-full">
                    <Clock className="h-3.5 w-3.5" /> {travels.workingHours}
                  </span>
                )}
                {travels.website && (
                  <a href={travels.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-white/15 hover:bg-white/25 backdrop-blur-sm ring-1 ring-white/20 px-3 py-1.5 rounded-full">
                    <Globe className="h-3.5 w-3.5" /> Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Contact Cards */}
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 -mt-16 animate-fade-up">
          {travels.phone && (
            <a href={'tel:' + travels.phone} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
              <div className="p-2.5 bg-green-50 rounded-lg"><Phone className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Call Us</p>
                <p className="text-sm font-semibold text-gray-900">{travels.phone}</p>
              </div>
            </a>
          )}
          {travels.email && (
            <a href={'mailto:' + travels.email} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: shade(brand, 42) }}><Mail className="h-5 w-5" style={{ color: brand }} /></div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{travels.email}</p>
              </div>
            </a>
          )}
          {(travels.address?.street || travels.address?.city) && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex items-center gap-3">
              <div className="p-2.5 bg-purple-50 rounded-lg"><MapPin className="h-5 w-5 text-purple-600" /></div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Address</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{[travels.address?.street, travels.address?.city].filter(Boolean).join(', ')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Trust badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {badges.isVerified && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-center">
              <ShieldCheck className="h-6 w-6 mx-auto mb-1.5 text-green-600" />
              <p className="text-sm font-bold text-gray-900">Verified</p>
              <p className="text-[11px] text-gray-500">Trusted partner</p>
            </div>
          )}
          {badges.sinceYear && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-center">
              <Calendar className="h-6 w-6 mx-auto mb-1.5" style={{ color: brand }} />
              <p className="text-sm font-bold text-gray-900">Since {badges.sinceYear}</p>
              <p className="text-[11px] text-gray-500">On {appConfig.name}</p>
            </div>
          )}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-center">
            <Route className="h-6 w-6 mx-auto mb-1.5" style={{ color: brand }} />
            <p className="text-sm font-bold text-gray-900">{badges.completedTrips || 0}+ Trips</p>
            <p className="text-[11px] text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-center">
            <Car className="h-6 w-6 mx-auto mb-1.5" style={{ color: brand }} />
            <p className="text-sm font-bold text-gray-900">{vehicles.length} Vehicles</p>
            <p className="text-[11px] text-gray-500">In fleet</p>
          </div>
        </div>

        {/* Services / What We Offer — derived from this agency's vehicles */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Our Services</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {services.map((s) => (
              <div key={s.key} className="text-center p-4 bg-gray-50 rounded-xl">
                <s.icon className="h-6 w-6 mx-auto mb-2" style={{ color: brand }} />
                <p className="text-sm font-semibold text-gray-900">{s.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Packages */}
        {packages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Our Packages</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map(pkg => {
                const finalPrice = pkg.basePrice - (pkg.basePrice * (pkg.discountPercentage || 0) / 100);
                return (
                  <div key={pkg._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                    <div className="px-4 py-3 flex items-center gap-2 border-b border-gray-100">
                      <div className="p-1.5 rounded-lg" style={{ backgroundColor: shade(brand, 42) }}>
                        <Package className="h-4 w-4" style={{ color: brand }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 truncate">{pkg.name}</h3>
                        <span className="text-[11px] text-gray-500 capitalize">{pkg.packageType}</span>
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-baseline gap-1.5 mb-3">
                        <span className="text-xl font-bold" style={{ color: brand }}>{formatCurrency(finalPrice)}</span>
                        {pkg.discountPercentage > 0 && (
                          <>
                            <span className="text-xs text-gray-400 line-through">{formatCurrency(pkg.basePrice)}</span>
                            <span className="text-[10px] font-medium px-1 py-0.5 bg-green-100 text-green-700 rounded">{pkg.discountPercentage}% off</span>
                          </>
                        )}
                      </div>
                      {[pkg.startPoint, ...(pkg.places || [])].filter(Boolean).length > 0 && (
                        <div className="flex items-start gap-1.5 text-xs text-gray-700 mb-3 rounded-lg p-2" style={{ backgroundColor: shade(brand, 46) }}>
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: brand }} />
                          <span className="font-medium leading-snug">{[pkg.startPoint, ...(pkg.places || [])].filter(Boolean).join('  →  ')}</span>
                        </div>
                      )}
                      <div className="space-y-1.5 text-xs text-gray-600 mb-3">
                        <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gray-400" /> {pkg.duration?.value} {pkg.duration?.unit}</div>
                        {pkg.includedKm > 0 && <div className="flex items-center gap-1.5"><Navigation className="h-3.5 w-3.5 text-gray-400" /> {pkg.includedKm} km included</div>}
                        {pkg.extraKmCharge > 0 && <div className="flex items-center gap-1.5"><Car className="h-3.5 w-3.5 text-gray-400" /> {formatCurrency(pkg.extraKmCharge)}/extra km</div>}
                      </div>
                      {pkg.description && <p className="text-xs text-gray-500 mb-3">{pkg.description}</p>}
                      {pkg.features?.length > 0 && (
                        <ul className="space-y-1 mb-4">
                          {pkg.features.map((f, i) => (
                            <li key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                              <Check className="h-3.5 w-3.5 flex-shrink-0" style={{ color: brand }} /> {f}
                            </li>
                          ))}
                        </ul>
                      )}
                      {travels.phone && (
                        <a
                          href={'tel:' + travels.phone}
                          className="mt-auto block text-center px-3 py-2 text-white text-xs font-medium rounded-lg transition-opacity hover:opacity-90"
                          style={{ backgroundColor: brand }}
                        >
                          Enquire / Book
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Vehicle Type Filter */}
        {vehicles.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Our Vehicles ({filteredVehicles.length})</h2>
            </div>
            {vehicleTypes.length > 2 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {vehicleTypes.map(type => {
                  const active = selectedType === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                      style={active ? { backgroundColor: brand, color: '#fff' } : { backgroundColor: '#f3f4f6', color: '#4b5563' }}
                    >
                      {type === 'all' ? 'All' : titleCase(type.replace('-', ' '))}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Vehicle Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
              {filteredVehicles.map(car => (
                <div key={car._id} className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  {/* Image */}
                  <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    {car.images && car.images.filter(img => img).length > 0 ? (
                      <img src={car.images.filter(img => img)[0]} alt={car.make + ' ' + car.model} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="h-16 w-16 text-gray-300" />
                      </div>
                    )}
                    {car.forSale && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded">FOR SALE</span>
                    )}
                    <span className="absolute top-2 right-2 px-2 py-0.5 text-white text-xs font-medium rounded-full capitalize" style={{ backgroundColor: brand }}>
                      {(car.vehicleType || 'car').replace('-', ' ')}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="p-4">
                    <h3 className="text-base font-bold text-gray-900 mb-1">{titleCase(car.make)} {titleCase(car.model)}</h3>
                    <p className="text-xs text-gray-500 mb-3">{car.year} · {car.rtoCode}</p>

                    <div className="grid grid-cols-3 gap-2 mb-3 text-xs text-gray-600">
                      <div className="flex items-center gap-1"><Users className="h-3 w-3" /> {car.seats || '-'} Seats</div>
                      <div className="flex items-center gap-1"><Fuel className="h-3 w-3" /> {car.fuelType || '-'}</div>
                      <div className="flex items-center gap-1"><Car className="h-3 w-3" /> {car.transmission || '-'}</div>
                    </div>

                    {car.location?.city && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
                        <MapPin className="h-3 w-3" /> {car.location.city}{car.location.state ? ', ' + car.location.state : ''}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div>
                        <span className="text-lg font-bold" style={{ color: brand }}>{formatCurrency(car.pricePerDay)}</span>
                        <span className="text-xs text-gray-400">/day</span>
                      </div>
                      <Link
                        to={'/browse?ownerId=' + ownerId}
                        className="px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-opacity hover:opacity-90"
                        style={{ backgroundColor: brand }}
                      >
                        Book Now
                      </Link>
                    </div>

                    {car.forSale && car.salePrice > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <span className="text-sm font-bold text-green-600">Sale: {formatCurrency(car.salePrice)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {vehicles.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <Car className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No vehicles listed yet.</p>
          </div>
        )}

        {/* Reviews */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
            <h2 className="text-lg font-bold text-gray-900">Customer Reviews</h2>
            {rating.totalReviews > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900">{rating.averageRating}</span>
                <div>
                  <StarRow value={rating.averageRating} />
                  <p className="text-xs text-gray-500">{rating.totalReviews} review{rating.totalReviews !== 1 ? 's' : ''}</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Review list */}
            <div className="lg:col-span-2">
              {reviews.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {reviews.map(rv => (
                    <div key={rv._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <StarRow value={rv.rating} size="h-3.5 w-3.5" />
                        <span className="text-[11px] text-gray-400">{new Date(rv.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      </div>
                      {rv.comment && <p className="text-sm text-gray-700 mb-2">“{rv.comment}”</p>}
                      <p className="text-xs font-semibold text-gray-900">— {rv.customerName}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-8 text-center h-full flex flex-col items-center justify-center">
                  <Star className="h-10 w-10 text-gray-200 mb-2" />
                  <p className="text-sm text-gray-500">No reviews yet. Be the first to review {travels.name}!</p>
                </div>
              )}
            </div>

            {/* Write a review */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 h-fit">
              <h3 className="text-sm font-bold text-gray-900 mb-3">
                {hasExistingReview ? 'Your Review' : 'Write a Review'}
              </h3>

              {!authUser ? (
                /* Logged-out state — invite login */
                <div className="text-center py-4">
                  <div className="w-12 h-12 mx-auto rounded-full bg-blue-50 flex items-center justify-center mb-3">
                    <LogIn className="h-6 w-6 text-blue-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Log in to write a review</p>
                  <p className="text-xs text-gray-500 mb-4">
                    Reviews are tied to your account so each customer has one honest rating per agency.
                  </p>
                  <Link
                    to={`/customer-login?next=${encodeURIComponent(location.pathname)}`}
                    className="inline-flex items-center justify-center gap-1.5 w-full py-2.5 text-white text-sm font-semibold rounded-lg transition-colors"
                    style={{ backgroundColor: brand }}
                  >
                    <LogIn className="h-4 w-4" /> Log in with OTP
                  </Link>
                </div>
              ) : reviewDone ? (
                <div className="text-center py-4">
                  <CheckCircle className="h-10 w-10 mx-auto mb-2" style={{ color: brand }} />
                  <p className="text-sm font-medium text-gray-900">Thank you!</p>
                  <p className="text-xs text-gray-500 mb-3">
                    {hasExistingReview ? 'Your review has been updated.' : 'Your review has been posted.'}
                  </p>
                  <button onClick={() => setReviewDone(false)} className="text-xs font-medium" style={{ color: brand }}>
                    Edit again
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitReview} className="space-y-3">
                  {/* Reviewer name shown read-only — pulled from logged-in user */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-blue-700">
                        {(authUser.name || authUser.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{authUser.name || 'Customer'}</p>
                      {authUser.email && <p className="text-[10px] text-gray-500 truncate">{authUser.email}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Your Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <button type="button" key={i} onClick={() => setReviewForm(f => ({ ...f, rating: i }))}>
                          <Star className="h-7 w-7" style={{ color: '#f59e0b', fill: i <= reviewForm.rating ? '#f59e0b' : 'transparent' }} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    value={reviewForm.comment}
                    onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder="Share your experience (optional)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
                  />
                  {reviewError && <p className="text-xs text-red-600">{reviewError}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 text-white text-sm font-medium rounded-lg disabled:opacity-60"
                    style={{ backgroundColor: brand }}
                  >
                    <Send className="h-4 w-4" />
                    {submitting
                      ? (hasExistingReview ? 'Updating…' : 'Posting…')
                      : (hasExistingReview ? 'Update Review' : 'Submit Review')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-2xl p-8 text-center text-white mb-8" style={heroGradient}>
          <h2 className="text-xl font-bold mb-2">Need a Vehicle?</h2>
          <p className="text-white/80 text-sm mb-4">Contact {travels.name} for the best rates and packages.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {travels.phone && (
              <a href={'tel:' + travels.phone} className="px-6 py-2.5 bg-white font-semibold rounded-xl hover:bg-white/90 transition-colors text-sm" style={{ color: brand }}>
                Call Now: {travels.phone}
              </a>
            )}
            <a href={'https://wa.me/' + (travels.phone || '').replace(/[^0-9]/g, '')} target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors text-sm">
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Footer — agency name primary, app credited below */}
      <div className="bg-gray-900 text-gray-300 py-7">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <div className="w-7 h-7 rounded-md flex items-center justify-center overflow-hidden" style={{ backgroundColor: travels.logo?.url ? 'transparent' : brand }}>
              {travels.logo?.url ? (
                <img src={travels.logo.url} alt={travels.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs font-bold text-white">{initial}</span>
              )}
            </div>
            <span className="text-base font-semibold text-white">{travels.name}</span>
          </div>
          {travels.phone && <p className="text-xs text-gray-400">{travels.phone}{travels.email ? ' · ' + travels.email : ''}</p>}
          <p className="text-[11px] text-gray-500 mt-3">Powered by <Link to="/home" className="text-gray-400 hover:underline">{appConfig.name}</Link></p>
        </div>
      </div>
    </div>
  );
};

export default TravelsProfile;
